import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

const HeroSection = () => {
  const navigate = useNavigate();
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isVideoLoaded) {
        setVideoError(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isVideoLoaded]);

  const handleProduct = () => navigate("/products");
  const handleContact = () => navigate("/contact");

  const stats = [
    { number: "100%", label: "Quality", suffix: "Assured" },
    { number: "24/7", label: "Technical", suffix: "Support" },
    { number: "Fast", label: "Delivery", suffix: "Guaranteed" },
    { number: "Expert", label: "Team", suffix: "Service" }
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 py-20">
      <div className="absolute inset-0">
        {!videoError ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
            onLoadedData={() => setIsVideoLoaded(true)}
            onError={() => setVideoError(true)}
            style={{ display: isVideoLoaded ? 'block' : 'none' }}
          >
            <source src="/images/homehero.mp4" type="video/mp4" />
            <source src="/images/homehero.mp4" type="video/webm" />
          </video>
        ) : (
          <div/>
        )}
        
        <div  />
        <div  />
        
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" />
        </div>

        <div className="absolute inset-0 overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${15 + Math.random() * 10}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 5}s`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative z-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-left">
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-xl rounded-2xl px-6 py-4 mb-8 border border-white/20 shadow-2xl">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full animate-pulse"></div>
                <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
              </div>
              <span className="text-sm font-semibold text-slate-900 tracking-widest uppercase">
                Premium Industrial Solutions
              </span>
            </div>

            {/* Main Heading - Improved Size */}
            <h1 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">
              <span className="block mb-3">Precision</span>
              <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 bg-clip-text text-transparent mb-3">
                Engineering
              </span>
              <span className="block">Excellence</span>
            </h1>
            
            {/* Improved Subtitle */}
            <p className="text-lg text-slate-900 mb-8 leading-relaxed font-light max-w-2xl">
              Delivering <span className="text-cyan-300 font-semibold">premium industrial machinery</span> 
              {" "}and solutions with cutting-edge technology, expert support, and uncompromising quality for your manufacturing success.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4 mb-12">
              <button
                onClick={handleProduct}
                className="group relative bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-4 rounded-xl font-bold text-base transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative flex items-center gap-2">
                  Explore Products
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </button>
              
              <button
                onClick={handleContact}
                className="group relative bg-transparent border-2 border-white/40 text-white px-8 py-4 rounded-xl font-bold text-base transition-all duration-300 hover:bg-white hover:text-slate-900 hover:border-white hover:shadow-xl active:scale-95 backdrop-blur-sm"
              >
                <span className="relative flex items-center gap-2">
                  Get Quote
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </span>
              </button>
            </div>
          </div>

          {/* Stats Section - Updated for New Company */}
          <div className="grid grid-cols-2 gap-4 lg:gap-6">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="group text-left p-6 bg-white/5 rounded-2xl backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:shadow-xl cursor-pointer"
              >
                <div className="text-1xl font-bold text-slate-900 mb-2 bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  {stat.number}
                </div>
                <div className="text-base font-semibold text-slate-900 mb-1">
                  {stat.label}
                </div>
                <div className="text-xs text-gray-600 font-medium">
                  {stat.suffix}
                </div>
                <div className="w-0 group-hover:w-full h-0.5 bg-cyan-400 transition-all duration-300 mt-2" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Elements */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex flex-col items-center gap-3">
          <span className="text-xs text-gray-400 font-medium tracking-widest uppercase">Scroll to Explore</span>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center p-1 backdrop-blur-sm">
            <div className="w-1 h-2 bg-gradient-to-b from-cyan-400 to-blue-400 rounded-full mt-2 animate-bounce" />
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="absolute bottom-20 left-6 hidden xl:block">
        <div className="bg-white/10 backdrop-blur-xl rounded-xl p-4 border border-white/20">
          <div className="text-white text-xs font-semibold mb-2 uppercase tracking-widest">Why Choose Us</div>
          <div className="space-y-1 text-gray-300 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
              <span>Premium Quality</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
              <span>Expert Support</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
              <span>Reliable Service</span>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {!isVideoLoaded && !videoError && (
        <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center z-30 backdrop-blur-sm">
          <div className="flex items-center gap-3 bg-white/10 backdrop-blur-xl rounded-2xl px-6 py-4 border border-white/20">
            <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-white font-semibold text-sm">Loading...</span>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;